import React, { FC, Fragment, useState } from 'react';
import { getShortLanguageCode } from 'utils/format';
import { Alert, Box, CircularProgress, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { addLabTemplate, organizationLookupTemplate } from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { geocodeAddress } from 'utils/google-places';
import LabForm from './lab-form.component';
import { LabFormData } from 'models/forms';
import OrganizationLookup, {
    OrganizationLookupFormData,
} from 'components/shared/organization-lookup/organization-lookup.component';
import { Lab } from 'models/lab';
import { getEnabledCountries } from 'utils/country-helper';
import { Country } from 'models/country';

type AddLabDialogProps = {
    dialogOpen: boolean;
    onCloseDialog: () => void;
    onLabAdded: () => void;
};
const AddLabDialog: FC<AddLabDialogProps> = ({ dialogOpen, onCloseDialog, onLabAdded }: AddLabDialogProps) => {
    const { t: translate } = useTranslation(['organization']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [activeStep, setActiveStep] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);
    const [lab, setLab] = useState<Partial<Lab>>();
    const [countryCode, setCountryCode] = useState<string>();
    const EnabledCountries = getEnabledCountries();
    const sweden = EnabledCountries.find((country) => country.alpha2Code === 'SE');
    const [country] = useState<Country | undefined>(sweden);

    const [organizationLookup] = useLazyQuery(organizationLookupTemplate, {
        fetchPolicy: 'network-only',
        onError: (error) => {
            setIsLoading(false);
            if (error.message.includes('HitHorizons: No results found')) {
                setErrorMessage(translate('organization:VALIDATION.ORGANIZATION_NOT_FOUND'));
                return;
            }
            if (error.message.includes('organization with same hitHorizonsId already exists')) {
                setErrorMessage(translate('organization:VALIDATION.ORGANIZATION_ALREADY_EXISTS'));
                return;
            }
            showBoundary(error);
        },
        onCompleted: async ({ organizationLookup }) => {
            organizationLookup = organizationLookup[0];
            setIsLoading(false);
            if (organizationLookup.existsInDb) {
                setErrorMessage(translate('organization:VALIDATION.ORGANIZATION_ALREADY_EXISTS'));
                return;
            }
            if (countryCode) {
                setLab({
                    hitHorizonsId: organizationLookup.hitHorizonsId,
                    name: organizationLookup.name,
                    type: organizationLookup.type,
                    officialName: organizationLookup.name,
                    organizationNumber: organizationLookup.organizationNumber,
                    contact: {
                        communication: { site: organizationLookup.site || undefined },
                        address: await geocodeAddress(organizationLookup.address, getShortLanguageCode()),
                    },
                    invoicing: {
                        vatNo: organizationLookup.vatNumber,
                    },
                });

                setActiveStep(1);
            }
        },
    });

    const [addLab] = useMutation(addLabTemplate, {
        onCompleted: ({}) => {
            onLabAdded();
        },
        onError(error) {
            handleCloseDialog();
            showBoundary(error);
        },
    });

    const handleOrganizationSelected = (selectOrganizationForm: OrganizationLookupFormData) => {
        setIsLoading(true);
        setCountryCode(selectOrganizationForm.countryCode);
        organizationLookup({
            variables: {
                organizationNumber: selectOrganizationForm.organizationNumber,
                countryCode: selectOrganizationForm.countryCode,
                checkWhitelabel: false,
            },
        });
    };

    const handleCloseDialog = () => {
        setActiveStep(0);
        setErrorMessage(undefined);
        setIsLoading(false);
        setLab(undefined);
        setCountryCode(undefined);
        onCloseDialog();
    };

    const handleFormSubmitted = (formData: LabFormData) => {
        setIsLoading(true);
        addLab({
            variables: {
                lab: {
                    name: formData?.name,
                    officialName: formData.officialName,
                    organizationNumber: formData.organizationNumber,
                    contact: {
                        address: formData?.address,
                        communication: {
                            phone: formData?.phone,
                            email: formData?.email,
                            site: formData?.site,
                        },
                    },
                    invoicing: {
                        vatNo: formData?.vatNo,
                    },
                    crediFlow: {
                        partyID: formData?.crediflowPartyId,
                    },
                    localeFormat: formData.localeFormat,
                },
            },
        });
    };

    const handleManualAddClicked = () => {
        setActiveStep(1);
    };

    const getStepContent = (stepIndex: number) => {
        switch (stepIndex) {
            case 0:
                return (
                    <OrganizationLookup
                        onOrganizationSelected={handleOrganizationSelected}
                        onClose={handleCloseDialog}
                        country={country}
                        onOrganizationChanged={() => setErrorMessage(undefined)}
                        allowManualAdd
                        onManualAddClicked={handleManualAddClicked}
                        manualTitle={translate('organization:ADD_LAB_MANUAL')}
                    />
                );
            case 1:
                return (
                    <Fragment>
                        <LabForm
                            lab={lab || {}}
                            onClose={handleCloseDialog}
                            onFormSubmitted={handleFormSubmitted}
                            submitButtonText={translate('organization:ADD_LAB')}
                        />
                    </Fragment>
                );
            default:
                break;
        }
    };

    return (
        <Dialog fullWidth maxWidth="md" open={dialogOpen}>
            <DialogTitle>{translate('organization:ADD_LAB')}</DialogTitle>
            <DialogContent>
                {errorMessage && (
                    <Box mb={2}>
                        <Alert severity="error">{errorMessage}</Alert>
                    </Box>
                )}
                {isLoading ? (
                    <Box sx={{ minHeight: '200px' }} display="flex" alignItems="center" justifyContent="center">
                        <CircularProgress size={80} />
                    </Box>
                ) : (
                    getStepContent(activeStep)
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AddLabDialog;
